'use client'

import React, { useId, useState } from 'react'
import { InputBase, InputLabel } from '../InputBase/InputBase'
import type { WithInputBaseProps } from '../InputBase/InputBase'
import { cn } from '@/utils'

export type InputPriceProps = {
  /**
   * Floating label displayed inside the input
   */
  label?: string
  /**
   * Extra className forwarded to the InputLabel element
   */
  labelClassName?: string
  /**
   * ISO 4217 currency code used by `Intl.NumberFormat` (e.g. "BRL", "USD", "EUR").
   */
  currency?: string
  /**
   * BCP 47 locale tag used by `Intl.NumberFormat` (e.g. "pt-BR", "en-US").
   */
  locale?: string
  /**
   * Number of decimal places. Defaults to 2 (cents). Use 0 for integer-only currencies.
   */
  precision?: number
} & WithInputBaseProps<React.ComponentProps<'input'>>

const digitsOnly = (raw: string) => String(raw).replace(/\D/g, '')

const formatPrice = (
  raw: string,
  currency: string,
  locale: string,
  precision: number,
): string => {
  const digits = digitsOnly(raw)
  if (!digits) return ''
  const value = Number(digits) / Math.pow(10, precision)
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    useGrouping: false,
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
  })
    .format(value)
    .replaceAll(String.fromCharCode(0xa0), ' ')
}

export const InputPrice = ({
  className,
  startAdornment,
  endAdornment,
  size,
  style,
  isRounded,
  variant,
  helperText,
  state,
  placeholder,
  label,
  labelClassName,
  inputContainerClassName,
  defaultValue,
  currency = 'BRL',
  locale = 'pt-BR',
  precision = 2,
  ...props
}: InputPriceProps) => {
  const triggerId = useId()
  const inputId = props.id || triggerId

  const isControlled = props.value !== undefined
  const [internalValue, setInternalValue] = useState(() =>
    formatPrice(String(defaultValue ?? ''), currency, locale, precision),
  )

  const displayValue = isControlled
    ? formatPrice(String(props.value), currency, locale, precision)
    : internalValue

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPrice(e.target.value, currency, locale, precision)
    if (!isControlled) setInternalValue(formatted)
    e.target.value = formatted
    props.onChange?.(e)
  }

  return (
    <InputBase
      size={size}
      variant={variant}
      isRounded={isRounded}
      state={state}
      disabled={props.disabled}
      helperText={helperText}
      startAdornment={startAdornment}
      endAdornment={endAdornment}
      className={cn('w-full p-0', className)}
      inputContainerClassName={cn(
        props.disabled && 'ring-gray-300',
        inputContainerClassName,
      )}
      style={style}
    >
      <input
        className={cn(
          'peer h-full w-full px-4 py-3 text-base font-medium leading-[120%] outline-transparent disabled:text-gray-500',
          {
            'pl-12': !!startAdornment,
            'pr-12': !!endAdornment,
            'not-focus:placeholder:text-transparent': !!label,
            'pt-8': !!label,
          },
        )}
        placeholder={placeholder || ' '}
        data-testid="input-price"
        {...props}
        value={displayValue}
        id={inputId}
        type="text"
        onChange={handleChange}
        disabled={props.disabled ?? false}
      />

      {label && (
        <InputLabel
          state={state}
          htmlFor={inputId}
          className={cn(
            state !== 'danger' && 'text-gray-400',
            'group-data-disabled:text-gray-400 leading-normal',
            labelClassName,
          )}
        >
          {label}
        </InputLabel>
      )}
    </InputBase>
  )
}
